/**
 * GroupChipBar — pasek chipów filtrujących listę kartoteki po GRUPACH ze słownika.
 *
 * Decyzja właścicielki 2026-08-18: nad listą pokazujemy chipy GRUP, nie kategorii-LAYOUTU
 * (`system_module_categories`, `CategoryChipBar`) — jej etykiety bywały zbieżne z grupami i dwa
 * paski chipów z tymi samymi nazwami myliły. NIE mylić z osobną osią kategorii-TYPU
 * (`record_categories`, `categoryId`, ADR-0093b) — to inny byt, na liście go dziś nie ma
 * (filtr/sort po kategorii odłożony do refactoru silnika list).
 *
 * Różnica względem `CategoryChipBar` (którego tu NIE zastępujemy w kodzie — nadal filtruje
 * kategorie tam, gdzie taki filtr został):
 *  - WIELOKROTNY wybór: kliknięcie kolejnego chipa DODAJE grupę do filtra (semantyka OR,
 *    „pokaż dostawców i partnerów"), nie przełącza na jedną.
 *  - „Wszystkie" = pusty filtr, czyści zaznaczenie.
 *  - Wartości to kody słownika (string), nie UUID kategorii.
 */

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { useDictionary } from '@/hooks/useDictionary';

export interface GroupChipBarProps {
  /** Kod słownika grup kartoteki, np. `employee_groups`. */
  dictionaryCode: string;
  /** Kody aktualnie wybranych grup. Pusta tablica = „Wszystkie". */
  selected: ReadonlyArray<string>;
  onChange: (groups: string[]) => void;
  className?: string;
  'data-testid'?: string;
}

export function GroupChipBar({
  dictionaryCode,
  selected,
  onChange,
  className,
  'data-testid': testId = 'group-chip-bar',
}: GroupChipBarProps) {
  const { t } = useTranslation();
  const { activeItems, isError } = useDictionary(dictionaryCode);

  // Awaria pobrania słownika MUSI być widoczna. Cichy `return null` wyglądał identycznie jak
  // „ta kartoteka nie ma grup" — przy błędzie backendu (np. schemat tenanta bez migracji)
  // użytkownik widział brak funkcji zamiast błędu i szukał usterki w złym miejscu.
  if (isError) {
    return (
      <div
        className={`text-sm text-amber-700 dark:text-amber-300 ${className ?? ''}`}
        data-testid={`${testId}-error`}
      >
        {t('tenant.groups.filter.loadError')}
      </div>
    );
  }

  // Słownik bez wybieralnych pozycji = nie ma po czym filtrować; nie zajmujemy miejsca paskiem.
  if (activeItems.length === 0) return null;

  function toggle(code: string) {
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  }

  // Chip = prymityw `Button` w wariancie `ghost` (bramka biblioteki UI, §4.12 — zero surowych
  // <button>). Redesign „Ant" (wg Figmy „Użytkownicy — Ant Design 18"): pasek to antowy
  // `Segmented` — szary track #f5f5f5 wokół wszystkich opcji, zaznaczona = BIAŁY uniesiony box.
  // Realny wygląd nadaje CSS w `index.css` (`[data-app-skin='antd'] [data-chip-bar='groups']`,
  // stan po `aria-pressed`); klasy poniżej to neutralny fallback. Kropka koloru = leftIcon.
  const chipClass = (active: boolean) =>
    `rounded-md ${active ? 'font-medium text-text-primary' : 'text-text-secondary hover:text-text-primary'}`;

  return (
    <div
      // Wielokrotny wybór → NIE role="tablist" (tab-list implikuje jeden aktywny tab).
      role="group"
      aria-label={t('tenant.groups.filter.label')}
      className={`flex flex-wrap items-center gap-2 ${className ?? ''}`}
      data-chip-bar="groups"
      data-testid={testId}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-pressed={selected.length === 0}
        onClick={() => onChange([])}
        className={chipClass(selected.length === 0)}
        data-testid={`${testId}-chip-all`}
      >
        {t('tenant.groups.filter.all')}
      </Button>
      {activeItems.map((item) => {
        const active = selected.includes(item.code);
        return (
          <Button
            key={item.code}
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={active}
            onClick={() => toggle(item.code)}
            className={chipClass(active)}
            leftIcon={
              item.color ? (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
              ) : undefined
            }
            data-testid={`${testId}-chip-${item.code}`}
          >
            {item.label}
          </Button>
        );
      })}
    </div>
  );
}
